import React from "react";

import { interpolateColors } from "../utils/colorGenerator";
import { interpolateTurbo } from "d3-scale-chromatic";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LoadingPackVisualization from "./LoadingPackVisualization";
import WeightBreakdown from "./WeightBreakdown";
import DoughnutChart from "./DoughnutChart";
import { LayoutDashboard, PieChart, X } from "lucide-react";
import {
  getBorder,
  getMutedText,
  HEADING_FONT,
} from "../constants/colors";
import { useMediaQuery } from "react-responsive";
import {
  getWeightInGrams,
  getWeightInUnitFromGrams,
  getWeightPercentage,
} from "../utils";

const colorScale = interpolateTurbo;
const colorRangeInfo = {
  colorStart: 0,
  colorEnd: 1,
  useEndAsStart: true,
};

// Clamp each palette color's lightness into a narrow band. Very bright
// segments optically swell and very dark ones shrink against the dark card
// (irradiation illusion), which reads as strip segments of uneven height
// even though their pixel geometry is identical.
const normalizeLightness = (rgbString) => {
  const match = rgbString.match(/rgba?\(([^)]+)\)/);
  if (!match) return rgbString;
  const [r, g, b] = match[1]
    .split(",")
    .map((value) => Number(value.trim()) / 255);
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const lightness = (max + min) / 2;
  const delta = max - min;
  let hue = 0;
  let saturation = 0;
  if (delta !== 0) {
    saturation =
      lightness > 0.5 ? delta / (2 - max - min) : delta / (max + min);
    if (max === r) hue = ((g - b) / delta + (g < b ? 6 : 0)) / 6;
    else if (max === g) hue = ((b - r) / delta + 2) / 6;
    else hue = ((r - g) / delta + 4) / 6;
  }
  // Soften the raw turbo palette into pastel territory: cap saturation well
  // below its near-100% values and lift lightness.
  const clampedSaturation = Math.min(0.45, Math.max(0.3, saturation));
  const clampedLightness = Math.min(0.7, Math.max(0.6, lightness));
  return `hsl(${Math.round(hue * 360)}, ${Math.round(
    clampedSaturation * 100
  )}%, ${Math.round(clampedLightness * 100)}%)`;
};

const PackVisualization = ({
  packDataArray,
  labels,
  unit,
  weightsInGrams,
  isShared,
  categoryDataArray,
  categoryLabelArray,
  toggleState,
  setToggleState,
  packIsLoading,
  setSideNavIsOpen,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [viewMode, setViewMode] = React.useState(
    () => localStorage.getItem("packVizStyle") || "treemap"
  );
  const [sliceLabelInfo, setSliceLabelInfo] = React.useState({
    label: "",
    weight: "",
  });

  const handleToggleViewMode = () => {
    const nextMode = viewMode === "treemap" ? "donut" : "treemap";
    setViewMode(nextMode);
    localStorage.setItem("packVizStyle", nextMode);
  };

  const dataArrayToUse = toggleState ? packDataArray : categoryDataArray;

  // Assign colors by stable (alphabetical) entity order, not by weight rank —
  // otherwise editing a weight re-sorts the list and every row changes color.
  const colorArray = React.useMemo(() => {
    const names = dataArrayToUse.map(({ item }, index) => item || `#${index}`);
    const sortedNames = [...names].sort((a, b) => a.localeCompare(b));
    const palette = interpolateColors(
      sortedNames.length,
      colorScale,
      colorRangeInfo
    ).map(normalizeLightness);
    const colorByName = {};
    sortedNames.forEach((name, index) => {
      if (!(name in colorByName)) colorByName[name] = palette[index];
    });
    return names.map((name) => colorByName[name]);
  }, [dataArrayToUse]);

  const donutRows = React.useMemo(() => {
    const rows = dataArrayToUse.map((row, index) => ({
      name: row.item,
      grams: getWeightInGrams(row.weight, row.unit) * row.quantity || 0,
      color: colorArray[index],
    }));
    const totalGrams = rows.reduce((acc, row) => acc + row.grams, 0) || 1;
    return [...rows]
      .sort((a, b) => b.grams - a.grams)
      .map((row) => ({ ...row, percent: (row.grams / totalGrams) * 100 }));
  }, [dataArrayToUse, colorArray]);

  const formatWeight = (grams) =>
    `${Number(getWeightInUnitFromGrams(grams || 0, unit)).toFixed(2)} ${unit}`;

  const stats = [
    { label: "Base", grams: weightsInGrams.baseInGrams, lead: true },
    { label: "Total", grams: weightsInGrams.weightInGrams },
    { label: "Worn", grams: weightsInGrams.wornInGrams },
    { label: "Consumables", grams: weightsInGrams.consumableInGrams },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        width: isMobile ? "700px" : "530px",
        height: isMobile ? "355px" : "245px",
        borderRadius: "16px",
        border: `1px solid ${getBorder(isDark)}`,
        backgroundImage: "none",
        backgroundColor: isDark ? "#262626" : "#FFFFFF",
      }}
    >
      {packIsLoading ? (
        <LoadingPackVisualization />
      ) : (
        <Stack
          sx={{
            height: "100%",
            padding: isMobile ? "12px 16px 12px 16px" : "10px 14px 10px 14px",
          }}
        >
          {/* Header: weight stats + controls — fixed, never shifts */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            gap="10px"
            sx={{ flexShrink: 0 }}
          >
            <Stack
              direction="row"
              gap={isMobile ? "26px" : "14px"}
              alignItems="flex-start"
              sx={{ minWidth: 0, overflow: "hidden" }}
            >
              {stats.map(({ label, grams, lead }) => (
                <Tooltip
                  key={label}
                  title={
                    label === "Total"
                      ? "Total pack weight"
                      : `${getWeightPercentage(
                          grams || 0,
                          weightsInGrams.weightInGrams || 1
                        )} of total`
                  }
                >
                  <Stack sx={{ cursor: "default", flexShrink: 0 }}>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 600,
                        fontSize: isMobile ? "12px" : "9.5px",
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: getMutedText(isDark),
                      }}
                    >
                      {label}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily: HEADING_FONT,
                        fontWeight: 700,
                        fontSize: lead
                          ? isMobile
                            ? "26px"
                            : "20px"
                          : isMobile
                          ? "19px"
                          : "14px",
                        lineHeight: lead ? 1.1 : isMobile ? 1.45 : 1.6,
                        letterSpacing: "-0.01em",
                      }}
                    >
                      {formatWeight(grams)}
                    </Typography>
                  </Stack>
                </Tooltip>
              ))}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              gap="4px"
              sx={{ flexShrink: 0 }}
            >
              <ToggleButtonGroup
                exclusive
                size="small"
                value={toggleState ? "items" : "categories"}
                onChange={(e, value) => {
                  if (value !== null) setToggleState(value === "items");
                }}
              >
                <ToggleButton
                  value="categories"
                  sx={{
                    textTransform: "none",
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : "11px",
                    padding: isMobile ? "5px 12px" : "2px 8px",
                  }}
                >
                  Categories
                </ToggleButton>
                <ToggleButton
                  value="items"
                  sx={{
                    textTransform: "none",
                    fontFamily: HEADING_FONT,
                    fontWeight: 600,
                    fontSize: isMobile ? "14px" : "11px",
                    padding: isMobile ? "5px 12px" : "2px 8px",
                  }}
                >
                  Items
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip
                title={
                  viewMode === "treemap"
                    ? "Switch to donut view"
                    : "Switch to treemap view"
                }
              >
                <IconButton size="small" onClick={handleToggleViewMode}>
                  {viewMode === "treemap" ? (
                    <PieChart size={18} />
                  ) : (
                    <LayoutDashboard size={18} />
                  )}
                </IconButton>
              </Tooltip>
              {Boolean(setSideNavIsOpen) && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSideNavIsOpen(false);
                  }}
                >
                  <X size={18} />
                </IconButton>
              )}
            </Stack>
          </Stack>
          {dataArrayToUse.length ? (
            viewMode === "donut" ? (
              <Stack
                direction="row"
                alignItems="center"
                gap={isMobile ? "16px" : "10px"}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ flexShrink: 0 }}>
                  <DoughnutChart
                    labels={toggleState ? labels : categoryLabelArray}
                    packDataArray={dataArrayToUse}
                    colorArray={colorArray}
                    sliceLabelInfo={sliceLabelInfo}
                    setSliceLabelInfo={setSliceLabelInfo}
                    isDesktopOrLaptop={false}
                  />
                </Box>
                <Stack
                  sx={{
                    flex: 1,
                    alignSelf: "stretch",
                    minWidth: 0,
                    minHeight: 0,
                    overflowY: "auto",
                    margin: "6px 0",
                    paddingRight: "4px",
                    "&::-webkit-scrollbar": { width: "6px" },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: isDark
                        ? "rgba(255, 255, 255, 0.2)"
                        : "rgba(0, 0, 0, 0.2)",
                      borderRadius: "3px",
                    },
                  }}
                >
                  {donutRows.map((row, index) => (
                    <Stack
                      key={`${row.name}-${index}`}
                      direction="row"
                      alignItems="center"
                      gap="8px"
                      sx={{
                        padding: isMobile ? "5px 8px" : "3px 6px",
                        borderRadius: "6px",
                        flexShrink: 0,
                        "&:hover": {
                          backgroundColor: isDark
                            ? "rgba(255, 255, 255, 0.06)"
                            : "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: "10px",
                          height: "10px",
                          borderRadius: "3px",
                          flexShrink: 0,
                          backgroundColor: row.color,
                        }}
                      />
                      <Typography
                        noWrap
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          fontFamily: HEADING_FONT,
                          fontWeight: 600,
                          fontSize: isMobile ? "15px" : "12px",
                        }}
                      >
                        {row.name}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontSize: isMobile ? "13px" : "11px",
                          color: getMutedText(isDark),
                          flexShrink: 0,
                        }}
                      >
                        {formatWeight(row.grams)}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: HEADING_FONT,
                          fontWeight: 700,
                          fontSize: isMobile ? "15px" : "12px",
                          width: isMobile ? "56px" : "46px",
                          textAlign: "right",
                          flexShrink: 0,
                        }}
                      >
                        {`${row.percent.toFixed(1)}%`}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Stack>
            ) : (
              <WeightBreakdown
                packDataArray={dataArrayToUse}
                colorArray={colorArray}
                unit={unit}
              />
            )
          ) : (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{ flex: 1 }}
            >
              <Typography
                textAlign="center"
                sx={{
                  fontFamily: HEADING_FONT,
                  fontSize: isMobile ? "18px" : "14px",
                  color: getMutedText(isDark),
                  maxWidth: "320px",
                }}
              >
                {isShared
                  ? "This pack has no items in it"
                  : "Add items to your pack to begin visualizing your base weight"}
              </Typography>
            </Stack>
          )}
        </Stack>
      )}
    </Paper>
  );
};

export default PackVisualization;
